import { useRef, useState } from "react";
import { CalendarDays, CheckCircle2, Download, FileSpreadsheet, FileText, Plus, Upload, XCircle } from "lucide-react";
import { calendarApi } from "../api";
import { useAsyncData, useMutation, usePageState } from "#/features/inventory/hooks";
import type { CalendarEvent, CalendarEventRequest, CalendarImportResult } from "../types";

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type FormState = {
  title: string;
  description: string;
  location: string;
  startLocal: string;
  endLocal: string;
  allDay: boolean;
};

const EMPTY_FORM: FormState = { title: "", description: "", location: "", startLocal: "", endLocal: "", allDay: false };

/** <input type="datetime-local"> gives "YYYY-MM-DDTHH:mm" in the browser's local time —
 *  treat it as local and let the Date constructor convert it to a UTC ISO string for the API. */
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Inverse of localToIso, for pre-filling the form when editing. */
function isoToLocal(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmt(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => CURRENT_YEAR + 5 - i);

export default function CalendarAdminPage() {
  const { page, size, setPage, setSize } = usePageState(10);
  const [year, setYear] = useState<number | null>(null);
  const { data, loading, error, reload } = useAsyncData(() => calendarApi.list(page, size, year), [page, size, year]);

  const [editing, setEditing] = useState<CalendarEvent | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation(async (payload: CalendarEventRequest) => {
    if (editing) return calendarApi.update(editing.id, payload);
    return calendarApi.create(payload);
  });

  const cancelMutation = useMutation((id: number) => calendarApi.cancel(id));

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowForm(true);
  }

  function openEdit(event: CalendarEvent) {
    setEditing(event);
    setForm({
      title: event.title,
      description: event.description ?? "",
      location: event.location ?? "",
      startLocal: isoToLocal(event.startAt),
      endLocal: isoToLocal(event.endAt),
      allDay: event.allDay,
    });
    setFormError(null);
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.title.trim()) return setFormError("Title is required.");
    const startAt = localToIso(form.startLocal);
    if (!startAt) return setFormError("Start date/time is required.");
    const endAt = localToIso(form.endLocal);
    if (endAt && new Date(endAt) < new Date(startAt)) return setFormError("End can't be before the start.");

    setFormError(null);
    try {
      await save.mutate({
        title: form.title.trim(),
        description: form.description.trim() || null,
        location: form.location.trim() || null,
        startAt,
        endAt,
        allDay: form.allDay,
      });
      setShowForm(false);
      setEditing(null);
      await reload();
    } catch (e: any) {
      setFormError(e?.response?.data?.message ?? e?.message ?? "Save failed.");
    }
  }

  async function handleCancelEvent(event: CalendarEvent) {
    await cancelMutation.mutate(event.id);
    await reload();
  }

  function handleYearChange(value: string) {
    setYear(value === "" ? null : Number(value));
    setPage(0);
  }

  // ── Bulk import (Excel template, Excel upload, .ics upload) ──────────────
  const [showImport, setShowImport] = useState(false);
  const [importResult, setImportResult] = useState<CalendarImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  const [importingIcs, setImportingIcs] = useState(false);
  const excelFileRef = useRef<HTMLInputElement>(null);
  const icsFileRef = useRef<HTMLInputElement>(null);

  async function handleDownloadTemplate() {
    setDownloadingTemplate(true);
    try {
      const blob = await calendarApi.downloadImportTemplate();
      triggerDownload(blob, "calendar-events-template.xlsx");
    } catch {
      /* silent */
    } finally {
      setDownloadingTemplate(false);
    }
  }

  async function handleExcelUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportingExcel(true);
    setImportError(null);
    try {
      const result = await calendarApi.importExcel(file);
      setImportResult(result);
      await reload();
    } catch (err: any) {
      setImportError(err?.response?.data?.message ?? err?.message ?? "Import failed.");
    } finally {
      setImportingExcel(false);
    }
  }

  async function handleIcsUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImportingIcs(true);
    setImportError(null);
    try {
      const result = await calendarApi.importIcs(file);
      setImportResult(result);
      await reload();
    } catch (err: any) {
      setImportError(err?.response?.data?.message ?? err?.message ?? "Import failed.");
    } finally {
      setImportingIcs(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarDays className="h-6 w-6 text-slate-400" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Company Calendar</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={year ?? ""}
            onChange={(e) => handleYearChange(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700"
          >
            <option value="">All years</option>
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={() => { setShowImport((v) => !v); setShowForm(false); }}
            className="flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <Upload className="h-4 w-4" />
            {showImport ? "Close Import" : "Import Events"}
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
          >
            <Plus className="h-4 w-4" />
            New Event
          </button>
        </div>
      </div>

      {showImport && (
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-bold text-slate-900">Import Events</h3>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex flex-col gap-2 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                1. Get the template
              </div>
              <button
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
              >
                <Download className="h-3.5 w-3.5" />
                {downloadingTemplate ? "Preparing…" : "Download Template (.xlsx)"}
              </button>
            </div>

            <div className="flex flex-col gap-2 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <FileSpreadsheet className="h-4 w-4 text-blue-600" />
                2. Upload Excel
              </div>
              <input ref={excelFileRef} type="file" accept=".xlsx" className="hidden" onChange={handleExcelUpload} />
              <button
                onClick={() => excelFileRef.current?.click()}
                disabled={importingExcel}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
              >
                <Upload className="h-3.5 w-3.5" />
                {importingExcel ? "Importing…" : "Upload Excel (.xlsx)"}
              </button>
            </div>

            <div className="flex flex-col gap-2 rounded-xl border border-slate-100 bg-slate-50/60 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <FileText className="h-4 w-4 text-purple-600" />
                Or upload .ics
              </div>
              <input ref={icsFileRef} type="file" accept=".ics" className="hidden" onChange={handleIcsUpload} />
              <button
                onClick={() => icsFileRef.current?.click()}
                disabled={importingIcs}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-xs font-semibold text-purple-700 hover:bg-purple-100 disabled:opacity-60"
              >
                <Upload className="h-3.5 w-3.5" />
                {importingIcs ? "Importing…" : "Upload Calendar (.ics)"}
              </button>
            </div>
          </div>

          {importError && (
            <p className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{importError}</p>
          )}

          {importResult && (
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-100">
              <div className="flex items-center justify-between bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700">
                <span>
                  {importResult.total} row{importResult.total !== 1 ? "s" : ""} — {importResult.success} imported,{" "}
                  {importResult.failed} failed
                </span>
              </div>
              {importResult.results.length > 0 && (
                <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto">
                  {importResult.results.map((r) => (
                    <div key={r.row} className="flex items-start gap-3 px-4 py-2.5 text-sm">
                      {r.success ? (
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                      ) : (
                        <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                      )}
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-slate-800">{r.title}</span>
                        {!r.success && r.error && (
                          <span className="ml-2 text-xs text-red-600">{r.error}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-bold text-slate-900">{editing ? "Edit Event" : "New Event"}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1.5 text-sm font-medium text-slate-700 md:col-span-2">
              Title <span className="text-red-500">*</span>
              <input className="input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="All-staff meeting" />
            </label>
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Starts <span className="text-red-500">*</span>
              <input type="datetime-local" className="input" value={form.startLocal} onChange={(e) => setForm({ ...form, startLocal: e.target.value })} />
            </label>
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Ends
              <input type="datetime-local" className="input" value={form.endLocal} onChange={(e) => setForm({ ...form, endLocal: e.target.value })} />
            </label>
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Location
              <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Boardroom / Zoom link" />
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={form.allDay} onChange={(e) => setForm({ ...form, allDay: e.target.checked })} />
              All day
            </label>
            <div className="md:col-span-2">
              <label className="space-y-1.5 text-sm font-medium text-slate-700">
                Description
                <textarea className="input min-h-20" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </label>
            </div>
          </div>
          {formError && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}
          <div className="mt-4 flex gap-2">
            <button onClick={handleSave} disabled={save.loading}
              className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {save.loading ? "Saving…" : editing ? "Update" : "Create"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-xl border px-4 py-2 text-sm font-semibold">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading && <div className="rounded-xl border bg-white p-5 text-sm text-slate-500">Loading events…</div>}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Starts</th>
                <th className="px-4 py-3">Ends</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.content ?? []).map((event) => (
                <tr key={event.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{event.title}</td>
                  <td className="px-4 py-3 text-slate-600">{fmt(event.startAt)}</td>
                  <td className="px-4 py-3 text-slate-500">{fmt(event.endAt)}</td>
                  <td className="px-4 py-3 text-slate-500">{event.location || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${event.cancelled ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {event.cancelled ? "Cancelled" : "Scheduled"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(event)} className="mr-3 text-sm font-semibold text-blue-600 hover:underline">Edit</button>
                    {!event.cancelled && (
                      <button onClick={() => handleCancelEvent(event)} className="text-sm font-semibold text-red-600 hover:underline">
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(data?.content ?? []).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No events yet. Create one to get started.</td></tr>
              )}
            </tbody>
          </table>
          <div className="border-t px-5 py-3 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              {data ? `${data.totalElements.toLocaleString()} total — Page ${data.number + 1} of ${Math.max(1, data.totalPages)}` : ""}
            </span>
            <div className="flex gap-2">
              <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="rounded-lg border border-slate-200 px-2 py-1.5">
                {[10, 25, 50].map((o) => <option key={o} value={o}>{o} rows</option>)}
              </select>
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={!data || data.first}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50">Prev</button>
              <button onClick={() => setPage(page + 1)} disabled={!data || data.last}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50">Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
